import React from "react";
import { formatDetectionConfidence } from "../../../common/dangerousWasteRecordDetections.js";

const ITEM_COLOR = "#22C55E";
const PICTOGRAM_COLOR = "#EF4444";
const TAG_TEXT_COLOR = "#111111";

// On-screen sizes, in CSS pixels, that hold at every zoom level.
const STROKE_PX = 2;
const FONT_PX = 11;
const TAG_PADDING_PX = 4;
// Montserrat's average glyph width at weight 600, close enough to size a tag behind its text.
const AVERAGE_GLYPH_WIDTH_EM = 0.62;

function DetectionBox({ box, color, text, unit, imageWidth }) {
    const fontSize = FONT_PX * unit;
    const padding = TAG_PADDING_PX * unit;
    const tagHeight = fontSize + padding * 2;
    const tagWidth = Math.min(
        text.length * fontSize * AVERAGE_GLYPH_WIDTH_EM + padding * 2,
        imageWidth,
    );
    // The tag sits on top of the box, or just inside it when the box touches the image's top edge.
    const tagY = box.y - tagHeight >= 0 ? box.y - tagHeight : box.y;
    const tagX = Math.min(Math.max(box.x, 0), imageWidth - tagWidth);

    return (
        <g>
            <rect
                x={box.x}
                y={box.y}
                width={box.w}
                height={box.h}
                fill="none"
                stroke={color}
                strokeWidth={STROKE_PX * unit}
            />
            {text && (
                <>
                    <rect x={tagX} y={tagY} width={tagWidth} height={tagHeight} fill={color} />
                    <text
                        x={tagX + padding}
                        y={tagY + tagHeight / 2}
                        dominantBaseline="central"
                        fill={TAG_TEXT_COLOR}
                        fontSize={fontSize}
                        fontFamily="Montserrat, sans-serif"
                        fontWeight={600}
                    >
                        {text}
                    </text>
                </>
            )}
        </g>
    );
}

/**
 * Item (green) and pictogram (red) boxes drawn over one camera image, in that image's own pixel
 * coordinates. The SVG fills the same box as the image and fits its viewBox the way the image
 * fits with object-fit: contain, so the two line up at any zoom, pan or rotation applied to both.
 */
export default function DetectionOverlay({ detections, screenPixelsPerImagePixel, itemLabel }) {
    const { width, height, itemBox, pictogramBoxes } = detections;
    if (!(screenPixelsPerImagePixel > 0)) return null;
    // One CSS pixel on screen, in image pixels.
    const unit = 1 / screenPixelsPerImagePixel;

    const labelFor = (label, confidence) => [label, formatDetectionConfidence(confidence)]
        .filter(Boolean)
        .join(" ");

    return (
        <svg
            className="detection-overlay"
            viewBox={`0 0 ${width} ${height}`}
            preserveAspectRatio="xMidYMid meet"
            aria-hidden="true"
        >
            {itemBox && (
                <DetectionBox
                    box={itemBox}
                    color={ITEM_COLOR}
                    text={labelFor(itemLabel, itemBox.confidence)}
                    unit={unit}
                    imageWidth={width}
                />
            )}
            {pictogramBoxes.map((box, i) => (
                <DetectionBox
                    key={i}
                    box={box}
                    color={PICTOGRAM_COLOR}
                    text={labelFor(box.label, box.confidence)}
                    unit={unit}
                    imageWidth={width}
                />
            ))}
        </svg>
    );
}
